<template>
  <f7-page name="group-dashboard" @page:init="onPageInit">
    <f7-navbar :title="groupName || $t('groupDashboard.title')" no-shadow back-link>
      <f7-nav-right>
        <f7-link
          icon-f7="arrow_clockwise"
          @click="refresh"
          :class="{ rotating: loading }"
        />
      </f7-nav-right>
    </f7-navbar>
    <AppLayout>
      <template #main>

        <f7-block v-if="loadError" class="dash-error">
          <f7-icon f7="exclamationmark_triangle_fill" />
          <span>{{ loadError }}</span>
          <f7-button small fill @click="load">{{ $t('common.retry') }}</f7-button>
        </f7-block>

        <div v-if="loading && !data" class="text-align-center" style="padding:32px 0">
          <f7-preloader />
          <p class="margin-top">{{ $t('groupDashboard.loading') }}</p>
        </div>

        <template v-else-if="data">
          <!-- avocash Group Lifecycle & Checklist Banner -->
          <div class="px-4 pt-3 pb-1">
            <div class="bg-surface-container-lowest rounded-2xl p-4 shadow-avocash-sm border border-outline/10 space-y-3">
              <div class="display-flex align-items-center justify-content-space-between">
                <div class="display-flex align-items-center gap-2">
                  <div class="w-8 h-8 rounded-full bg-primary/10 display-flex align-items-center justify-content-center text-primary">
                    <f7-icon material="published_with_changes" :size="18" />
                  </div>
                  <div>
                    <h4 class="font-headline-sm text-on-surface font-bold text-sm">Cycle d'activation</h4>
                    <p class="font-label-sm text-on-surface-variant">STATUT : ACTIVE_COMMUNITY</p>
                  </div>
                </div>
                <span class="display-inline-flex align-items-center px-2.5 py-0.5 rounded-full bg-secondary-container text-on-secondary-container font-label-sm font-semibold">
                  40%
                </span>
              </div>

              <div class="lifecycle-steps grid grid-cols-5 gap-1.5 h-2 width-100">
                <div class="rounded-full bg-primary h-full"></div>
                <div class="rounded-full bg-primary h-full"></div>
                <div class="rounded-full bg-secondary-container h-full animate-pulse"></div>
                <div class="rounded-full bg-surface-container-highest h-full"></div>
                <div class="rounded-full bg-surface-container-highest h-full"></div>
              </div>

              <div class="bg-surface-container-low p-3 rounded-xl display-flex align-items-center gap-2 text-on-surface-variant font-label-sm">
                <span class="material-symbols-outlined text-primary" style="font-size: 18px;">shield_with_heart</span>
                <span>Gouvernance financière & approbations trésorier prêtes à configurer.</span>
              </div>
            </div>
          </div>

          <GroupMetricGrid :data="data" />
          <GroupCampaignsSection :data="data" />
          <GroupPayoutsSection :data="data" />
          <GroupCampaignTransactionsSection :group-id="groupId" />
          <GroupMpSection :data="data" />
          <GroupEventsSection :data="data" />
          <GroupAccessSection :data="data" />

          <f7-block strong inset class="text-align-center">
            <small class="text-color-gray">{{ lastUpdate }}</small>
          </f7-block>
        </template>

        <div v-else class="text-align-center">
          <f7-icon f7="chart_bar_square" size="48" class="text-color-gray" />
          <p class="text-color-gray margin-top">{{ $t('groupDashboard.noData') }}</p>
        </div>

      </template>
    </AppLayout>
  </f7-page>
</template>

<style scoped>
.dash-error {
  display: flex;
  align-items: center;
  gap: 10px;
  background: color-mix(in srgb, var(--f7-theme-color) 10%, transparent);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--f7-theme-color);
  font-size: 13px;
}
.dash-error .button { flex-shrink: 0; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.rotating { animation: spin 1s linear infinite; }
.lifecycle-steps {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
  height: 2px;
}
</style>

<script setup>
import { ref, computed, onMounted } from 'vue';
import AppLayout from '../components/common/AppLayout.vue';
import { useDashboardMetrics } from '../js/composables/useDashboardMetrics.js';
import { useGroupsStore } from '../js/pinia-stores/group.js';

import GroupMetricGrid from '../components/dashboard/GroupMetricGrid.vue';
import GroupCampaignsSection from '../components/dashboard/GroupCampaignsSection.vue';
import GroupPayoutsSection from '../components/dashboard/GroupPayoutsSection.vue';
import GroupCampaignTransactionsSection from '../components/dashboard/GroupCampaignTransactionsSection.vue';
import GroupMpSection from '../components/dashboard/GroupMpSection.vue';
import GroupEventsSection from '../components/dashboard/GroupEventsSection.vue';
import GroupAccessSection from '../components/dashboard/GroupAccessSection.vue';

import i18n from '../js/i18n.js';
const { t } = i18n.global;

const props = defineProps({
  f7route: Object,
  f7router: Object,
});

const groupId = computed(() => props.f7route?.params?.groupId || '');
const {
  groupMetrics: data,
  groupLoading: loading,
  groupError,
  fetchGroupCurrent,
  refreshGroupDaily,
} = useDashboardMetrics();

const groupStore = useGroupsStore();
const loadError = ref(null);
const groupName = computed(() => {
  const g = groupStore.activeGroup;
  return g && g.name ? g.name : '';
});

const lastUpdate = computed(() => {
  const d = data.value;
  const date = d && d.metricDate;
  return date ? t('groupDashboard.lastUpdated') + ' ' + date : '';
});

async function load() {
  loadError.value = null;
  if (!groupId.value) return;
  try {
    await groupStore.fetchGroupDetail(groupId.value);
    await fetchGroupCurrent(groupId.value);
  } catch (err) {
    const msg = (err && (err.response && err.response.data && err.response.data.message) || (err && err.message)) || t('groupDashboard.noData');
    loadError.value = msg;
  }
}

async function refresh() {
  if (!groupId.value) return;
  try {
    await refreshGroupDaily(groupId.value);
  } catch (err) {
    loadError.value = err && (err.response && err.response.data && err.response.data.message) || (err && err.message);
  }
}

function onPageInit() {
  load();
}

onMounted(() => {
  load();
});
</script>